/**
 * Stores > Setup > Paperwork at /stores/setup/paperwork (ISD-575).
 *
 * Store Ops tracker for the paperwork stores send from The Sweet Spot: one row
 * per active store, one column per paperwork category, with how many came in
 * for the picked month. The categories and their monthly counts (the rules)
 * come from the api, so this page never hardcodes them; the subtitle and the
 * column headers are built from `data.rules`. Each count links into that
 * store's Store Info files at the matching Paperwork/<category> folder. A
 * store short on any category with a monthly count is in red; the "Missing
 * only" chip narrows the grid to those stores. The same shortfalls are emailed
 * to Store Ops on the 1st, 15th and 21st.
 *
 * Backed by GET /v1/stores/hq/paperwork?period=YYYY-MM. Gated by `stores.info`,
 * the same grant as the Store Info file tree it links into.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { isShort, usePaperworkOverview, type PaperworkOverview } from '@/lib/usePaperwork';
import { AccessDeniedCard, ErrorCard, StoresHeader } from './storesShared';

const inputClass =
  'rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink ' +
  'focus:border-amp-olive focus:outline-none ' +
  'dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

/** This month in Denver, as YYYY-MM (stores and the backend count in Denver time). */
function denverPeriod(): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  return `${y}-${m}`;
}

type SortColumn = 'store' | 'status';

const SUBTITLE_TAIL = "Counts link to each store's Paperwork folders in Store Info.";

/** "A, B and C" from a list of parts. */
function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Page subtitle built from the api's rules, e.g. "CINTAS (2 a month), Pest Control (1 a month) and Licenses sent from The Sweet Spot. ..." */
function paperworkSubtitle(rules: PaperworkOverview['rules'] | undefined): string {
  if (!rules || rules.length === 0) return `Paperwork sent from The Sweet Spot. ${SUBTITLE_TAIL}`;
  const parts = rules.map((r) => (r.need > 0 ? `${r.label} (${r.need} a month)` : r.label));
  return `${joinList(parts)} sent from The Sweet Spot. ${SUBTITLE_TAIL}`;
}

export function StorePaperwork() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.info', user?.superAdmin);
  const [params, setParams] = useSearchParams();
  const rawPeriod = params.get('period') ?? '';
  const period = /^\d{4}-\d{2}$/.test(rawPeriod) ? rawPeriod : denverPeriod();
  const missingOnly = params.get('missing') === '1';
  const sortBy: SortColumn = params.get('sort') === 'status' ? 'status' : 'store';
  const sortDir: SortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';

  const setParam = (key: string, value: string | undefined) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  const toggleSort = (col: SortColumn) => {
    if (sortBy !== col) {
      setParam('sort', col);
      setParam('dir', 'asc');
    } else setParam('dir', sortDir === 'asc' ? 'desc' : 'asc');
  };

  const q = usePaperworkOverview(period);
  if (user && !canView) return <AccessDeniedCard what="Paperwork" />;

  const data = q.data;
  const rules = data?.rules ?? [];
  const all = data?.stores ?? [];
  const shortCount = all.filter((s) => isShort(s.counts, rules)).length;
  const rows = all
    .filter((s) => !missingOnly || isShort(s.counts, rules))
    .sort((a, b) => {
      const d =
        sortBy === 'status'
          ? Number(isShort(b.counts, rules)) - Number(isShort(a.counts, rules)) || a.store_no.localeCompare(b.store_no)
          : a.store_no.localeCompare(b.store_no, undefined, { numeric: true });
      return sortDir === 'asc' ? d : -d;
    });

  const toolbar = (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <FilterLabel htmlFor="paperwork-period">Month</FilterLabel>
        <input
          id="paperwork-period"
          type="month"
          value={period}
          onChange={(e) => setParam('period', e.target.value || undefined)}
          className={inputClass}
        />
      </div>
      <Pill
        variant={missingOnly ? 'selected' : 'outline'}
        aria-pressed={missingOnly}
        onClick={() => setParam('missing', missingOnly ? undefined : '1')}
      >
        Missing only{data ? ` (${shortCount})` : ''}
      </Pill>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <StoresHeader
        title="Paperwork"
        subtitle={paperworkSubtitle(data?.rules)}
      />
      <DataGridShell total={rows.length} loaded={rows.length} countNoun="stores" toolbar={toolbar}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Store"
                sorted={sortBy === 'store' ? sortDir : null}
                onToggle={() => toggleSort('store')}
                thClassName="px-3 py-2"
              />
              {rules.map((r) => (
                <th key={r.category} className="px-3 py-2 text-left font-medium">
                  {r.label}
                  {r.need > 0 ? ` (${r.need} a month)` : ''}
                </th>
              ))}
              <SortableHeader
                label="Status"
                sorted={sortBy === 'status' ? sortDir : null}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2 w-[140px]"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {q.isLoading ? (
              <tr>
                <td colSpan={rules.length + 2} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading paperwork...
                </td>
              </tr>
            ) : q.error ? (
              <tr>
                <td colSpan={rules.length + 2} className="px-4 py-6">
                  <ErrorCard message={q.error.message} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={rules.length + 2} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {missingOnly ? 'Every store is up to date for this month.' : 'No stores to show.'}
                </td>
              </tr>
            ) : (
              rows.map((s) => <PaperworkRow key={s.store_no} store={s} rules={rules} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function PaperworkRow({
  store,
  rules,
}: {
  store: PaperworkOverview['stores'][number];
  rules: PaperworkOverview['rules'];
}) {
  const short = isShort(store.counts, rules);
  return (
    <tr className="align-top hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2">
        <Link
          to={`/stores/setup/info?store=${encodeURIComponent(store.store_no)}`}
          className="font-medium text-amp-ink hover:underline dark:text-amp-dark-ink"
        >
          {store.store_no} - {store.store_name}
        </Link>
      </td>
      {rules.map((r) => {
        const c = store.counts.find((x) => x.category === r.category);
        const have = c?.have ?? 0;
        const missing = r.need > 0 && have < r.need;
        const text = r.need > 0 ? `${have} of ${r.need}` : String(have);
        const cls = missing ? 'font-medium text-red-700 dark:text-red-300' : 'text-amp-ink dark:text-amp-dark-ink';
        return (
          <td key={r.category} className="px-3 py-2">
            {c?.folder_id ? (
              <Link
                to={`/stores/setup/info/${encodeURIComponent(store.store_no)}/files?folder=${c.folder_id}`}
                className={`${cls} hover:underline`}
              >
                {text}
              </Link>
            ) : (
              <span className={cls}>{text}</span>
            )}
          </td>
        );
      })}
      <td className="px-3 py-2">
        {short ? (
          <span className="font-medium text-red-700 dark:text-red-300">Missing</span>
        ) : (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Up to date</span>
        )}
      </td>
    </tr>
  );
}
